import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { TreeItem, TreeView } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IKeywordRisk from '@models/ITechSearchResults';
import { Icon } from '@iconify/react';
import { determineRiskType } from '../functions';
import { capitalizeFirstLetter } from '../../../../shared/functions';
import { RiskLevel } from '../../../../shared/types';

interface TechExtendedRiskIndicatorProps {
  keywordRisks: IKeywordRisk[];
}

export const TechExtendedRiskIndicator: FC<TechExtendedRiskIndicatorProps> = ({
  keywordRisks,
}) => {
  const ref = useRef();

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    function getExpandedItems() {
      if (keywordRisks && keywordRisks.length > 0) {
        return [keywordRisks[0].keyword];
      }

      return [''];
    }

    if (keywordRisks && keywordRisks.length > 0) {
      setExpandedItems(getExpandedItems());
    } else {
      setExpandedItems([]);
    }
  }, [keywordRisks]);

  const handleToggle = (event: SyntheticEvent, nodeIds: string[]) => {
    setExpandedItems(nodeIds);
  };

  return (
    <Stack
      spacing={3}
      sx={{ px: 2, maxWidth: 400 }}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <Stack direction={'row'} spacing={1} alignItems={'center'}>
        <Icon icon={'bx:chip'} fontSize={30} color={'grey'} />
        <Typography variant={'h6'}>Related Technology Risks</Typography>
      </Stack>
      <Divider variant={'middle'} sx={{ width: '100%' }} />
      <TreeView
        ref={ref}
        expanded={expandedItems}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        onNodeToggle={handleToggle}
      >
        {keywordRisks.map((risk, i) => (
          <TreeItem
            nodeId={risk.keyword}
            key={risk.keyword}
            label={
              <Typography
                sx={{
                  p: 0,
                  fontWeight: expandedItems.includes(risk.keyword) ? 700 : 400,
                }}
              >
                {capitalizeFirstLetter(risk.keyword)}
              </Typography>
            }
          >
            {risk.risks.map((level) => (
              <TreeItem
                nodeId={risk.keyword + level.level}
                key={risk.keyword + level.level}
                label={
                  <Stack direction={'row'} spacing={1}>
                    <Typography
                      sx={{
                        color: determineRiskType([{ level: level.level }])
                          .color,
                        fontWeight:
                          determineRiskType([{ level: level.level }]).level ===
                          RiskLevel.Extreme
                            ? 700
                            : 400,
                        p: 0,
                      }}
                    >
                      {`${level.level}`}
                    </Typography>
                    <Typography>
                      {`(${Number(level.score?.toPrecision(1))})`}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </TreeItem>
        ))}
      </TreeView>
      <Divider variant={'middle'} sx={{ width: '100%' }} />
    </Stack>
  );
};
